<template lang="pug">
el-dialog(
  title="Select Sticker"
  top="5vh"
  width="60%"
  class="el-dialog__max-w-700"
  :close-on-click-modal="false"
  v-model="dialogVisible"
)
  .stickers-selector
    .stickers-selector-header
      el-input(
        v-model="searchTerm"
        placeholder="Search stickers"
        @input="debouncedSearch"
        clearable
      )
        template(#suffix)
          fai(type="fal" icon="search")

    .stickers-list(v-loading="loading")
      .empty-state(v-if="!stickers.length && !loading")
        p No stickers found

      .stickers-list-view(v-else)
        .sticker-list-item(
          v-for="sticker in stickers"
          :key="sticker.id"
          :class="{ 'selected': selectedSticker === sticker.id }"
          @click="selectSticker(sticker.id)"
        )
          .sticker-list-item-content
            .sticker-thumbnail-small
              img(
                v-if="sticker.url"
                :src="sticker.url"
              )
              .no-image(v-else) No image
            .sticker-details
              .sticker-title {{ sticker.title }}
              .sticker-creator-info
                span.sticker-org(v-if="sticker.organization")
                  | {{ sticker.organization.name }}
                span.sticker-creator(v-else-if="sticker.user")
                  | {{ sticker.user.firstname }} {{ sticker.user.lastname }}

    .pagination-controls(v-if="stickers.length")
      .simple-pagination.d-flex.align-items-center
        .page-nav.d-flex
          el-button(
            @click="handleCurrentChange(currentPage - 1)"
            :disabled="currentPage === 1 || loading"
            size="small"
          ) Prev
          .page-numbers.mx-2 {{ currentPage }}
          el-button(
            @click="handleCurrentChange(currentPage + 1)"
            :disabled="!hasMorePages || loading"
            size="small"
          ) Next
        .page-info.ms-3(v-if="stickers.length > 0")
          | Showing {{ startItem }}-{{ endItem }}

  template(#footer)
    .dialog-footer
      el-button(@click="handleCancel") Cancel
      el-button(type="primary" @click="handleConfirm" :disabled="!selectedSticker") Confirm
</template>

<script lang="ts">
import {
  defineComponent, ref, computed, watch, onMounted,
} from 'vue';
import { ElMessage } from 'element-plus';
import { findPublishedStickersShortDetails } from '@/graphs/gql';
import { makeRequest } from '@/graphs/index';

// Using a simple debounce implementation
function debounce(func: (...args: any[]) => void, wait: number) {
  let timeout: NodeJS.Timeout;
  return function (...args: any[]) {
    clearTimeout(timeout);
    timeout = setTimeout(() => func.apply(this, args), wait);
  };
}

export default defineComponent({
  name: 'DialogSelectSingleSticker',
  props: {
    modelValue: {
      type: Boolean,
      default: false,
    },
    preSelectedSticker: {
      type: String,
      default: '',
    },
  },
  emits: ['update:modelValue', 'confirm', 'clear'],
  setup(props, { emit }) {
    const dialogVisible = computed({
      get: () => props.modelValue,
      set: (value) => emit('update:modelValue', value),
    });

    const stickers = ref([]);
    const loading = ref(false);
    const searchTerm = ref('');
    const currentPage = ref(1);
    const pageSize = ref(20);
    const hasMorePages = ref(true);
    const selectedSticker = ref(props.preSelectedSticker || '');

    // Define fetchStickers function
    const fetchStickers = async () => {
      loading.value = true;
      try {
        const result = await makeRequest(
          'query',
          findPublishedStickersShortDetails,
          {
            take: pageSize.value,
            skip: (currentPage.value - 1) * pageSize.value,
            searchString: searchTerm.value || undefined,
            filter: ['Active'],
            socialShopUserId: null,
          },
          null,
        );

        if (result?.data?.findPublishedStickersShortDetails) {
          stickers.value = result.data.findPublishedStickersShortDetails;
          hasMorePages.value = result.data.findPublishedStickersShortDetails.length === pageSize.value;
        }
      } catch (error) {
        console.error('Error fetching stickers:', error);
        ElMessage.error('Failed to load stickers');
      } finally {
        loading.value = false;
      }
    };

    // Watch for dialog visibility to fetch data and reset selections when opened
    watch(() => props.modelValue, (isOpen) => {
      if (isOpen) {
        selectedSticker.value = props.preSelectedSticker || '';
        fetchStickers();
      }
    });

    // Watch for changes to preSelectedSticker
    watch(() => props.preSelectedSticker, (newVal) => {
      selectedSticker.value = newVal || '';
    });

    const debouncedSearch = debounce(() => {
      currentPage.value = 1;
      hasMorePages.value = true;
      fetchStickers();
    }, 300);

    const handleCurrentChange = (page: number) => {
      if (page < 1 || (page > currentPage.value && !hasMorePages.value)) {
        return;
      }
      currentPage.value = page;
      fetchStickers();
    };

    const startItem = computed(() => {
      const offset = (currentPage.value - 1) * pageSize.value;
      return offset + 1;
    });

    const endItem = computed(() => {
      const offset = (currentPage.value - 1) * pageSize.value;
      return offset + stickers.value.length;
    });

    const selectSticker = (id: string) => {
      selectedSticker.value = id;
    };

    const handleConfirm = () => {
      const selectedStickerObject = stickers.value.find((s: any) => s.id === selectedSticker.value);
      emit('confirm', selectedSticker.value, selectedStickerObject);
      dialogVisible.value = false;
    };

    const handleCancel = () => {
      dialogVisible.value = false;
    };

    onMounted(() => {
      if (props.modelValue) {
        fetchStickers();
      }
    });

    return {
      dialogVisible,
      stickers,
      loading,
      searchTerm,
      currentPage,
      pageSize,
      hasMorePages,
      selectedSticker,
      startItem,
      endItem,
      debouncedSearch,
      handleCurrentChange,
      selectSticker,
      handleConfirm,
      handleCancel,
    };
  },
});
</script>

<style lang="scss" scoped>
.stickers-selector {
  display: flex;
  flex-direction: column;
  height: 60vh;
}

.stickers-selector-header {
  margin-bottom: 20px;
}

.stickers-list {
  flex: 1;
  overflow-y: auto;
  min-height: 300px;
}

.empty-state {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 200px;
  color: #909399;
}

.stickers-list-view {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.sticker-list-item {
  border: 1px solid #ebeef5;
  border-radius: 4px;
  padding: 10px;
  cursor: pointer;
  position: relative;
  transition: all 0.3s;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.sticker-list-item:hover {
  box-shadow: 0 2px 12px 0 rgba(0, 0, 0, 0.1);
}

.sticker-list-item.selected {
  border-color: #1EBEAF;
  background-color: rgba(30, 190, 175, 0.05);
}

.sticker-list-item-content {
  display: flex;
  align-items: center;
  gap: 12px;
  flex: 1;
}

.sticker-thumbnail-small {
  width: 50px;
  height: 50px;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  border-radius: 4px;
  flex-shrink: 0;
}

.sticker-thumbnail-small img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.no-image {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: #f5f7fa;
  color: #909399;
}

.sticker-details {
  display: flex;
  flex-direction: column;
  justify-content: center;
  flex: 1;
  min-width: 0;
}

.sticker-title {
  font-weight: bold;
  margin-bottom: 4px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.sticker-creator-info {
  display: flex;
}

.sticker-org, .sticker-creator {
  font-size: 0.85em;
  color: #606266;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.pagination-controls {
  margin-top: 20px;
  display: flex;
  justify-content: center;
}

.simple-pagination {
  display: flex;
  align-items: center;
}

.page-nav {
  display: flex;
  align-items: center;
}

.page-numbers {
  margin: 0 10px;
  font-weight: bold;
}

.page-info {
  margin-left: 15px;
  color: #606266;
  font-size: 0.9em;
}

.dialog-footer {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  margin-top: 20px;
}
</style>
